import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export type MenuItem = {
  label: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  /** Renders a hairline separator instead of a row. */
  separator?: boolean;
};

type MenuProps = {
  button: ReactNode;
  items: MenuItem[];
  align?: 'left' | 'right';
  width?: number;
  'data-testid'?: string;
};

/**
 * Lightweight stand-in for upstream's `Menu` (which wraps react-aria's
 * Disclosure/Popover). Closes on outside click and Escape.
 */
export function Menu({
  button,
  items,
  align = 'right',
  width = 200,
  ...rest
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0" data-testid={rest['data-testid']}>
      <span
        role="button"
        tabIndex={-1}
        className="inline-flex"
        onClick={() => setOpen(value => !value)}
      >
        {button}
      </span>
      {open && (
        <ul
          className={clsx(
            'absolute top-[calc(100%+4px)] z-50 overflow-hidden rounded-sm border border-menu-border',
            'bg-menu-background py-1 text-sm text-menu-item-text shadow-large',
            align === 'right' ? 'right-0' : 'left-0',
          )}
          style={{ width }}
        >
          {items.map((item, index) =>
            item.separator ? (
              <li
                key={`sep-${index}`}
                className="my-1 h-px bg-menu-border"
                aria-hidden="true"
              />
            ) : (
              <li key={index}>
                <button
                  type="button"
                  disabled={item.disabled}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left',
                    'hover:bg-menu-item-background-hover',
                    item.disabled && 'cursor-not-allowed opacity-50',
                  )}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect?.();
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
